import { builderProductValues, type BuilderProduct, type BuilderProducts } from "api-types/builders/builderTypes";
import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import { clientBuilderUserPermissionsWithResourceScopedFallback } from "api-types/builderUsers/builderUsersBaseTypes";
import type {
  ClientBuilderUserHydratedWithRoles,
  ClientBuilderUserPermission,
  ClientBuilderUserWithRoles,
} from "api-types/builderUsers/builderUsersResponses";
import type { ClientBuilderRole } from "api-types/permissions/permissionsResponses";
import type { UserAdminRole, UserAdminOnlyRole } from "api-types/users/userAdminRole";
import type { ClientUser, ClientUserHydrated, ClientUserHydratedWithRoles } from "api-types/users/usersResponses";
import type { AuthorizationArgs } from "common/cedar";
import { isAuthorized } from "common/cedar";
import type { PolicyJson } from "common/cedar/cedarTypes";
import { type FeatureName, type FeatureSet, featureNames, features } from "common/constants/features";
import { getSharedEnvironment } from "common/utils/env";
import { isHigharcDemoAccountEmail } from "common/utils/higharcDemoAccountEmail";

/**
 * What any user interface that prompts an account invite, etc should default to.
 */
export const DEFAULT_BUILDER_USER_ROLE_NAME = "Sales Manager";

/*
 * Used when displaying events that are triggered by someone without an account,
 * e.g. "A customer approved the home plan".
 */
export const ANONYMOUS_CUSTOMER: Pick<ClientUser, "firstName" | "lastName"> = {
  firstName: "A",
  lastName: "customer",
} as const;

// The following constants are used to map legacy roles to new (cedar-based) roles.
// All builders will be created with these roles by default, which correspond 1:1 to the legacy roles.
export type DefaultRoleName =
  | "Admin"
  | "Drafter"
  | "Field Staff"
  | "Sales Agent"
  | "Sales Manager"
  | "Sales Team Lead"
  | "Estimator"
  | "Estimating Manager";
export const defaultBuilderRoles: { name: DefaultRoleName; description: string }[] = [
  {
    name: "Admin",
    description: "Built-in Administrator role",
  },
  {
    name: "Drafter",
    description: "Built-in Drafter role",
  },
  {
    name: "Field Staff",
    description: "Built-in Field Staff role",
  },
  {
    name: "Sales Agent",
    description: "Built-in Sales Agent role",
  },
  {
    name: "Sales Manager",
    description: "Built-in Sales Manager role",
  },
  {
    name: "Sales Team Lead",
    description: "Built-in Sales Team Lead role",
  },
  {
    name: "Estimator",
    description: "Built-in Estimator role",
  },
  {
    name: "Estimating Manager",
    description: "Built-in Estimating Manager role",
  },
];

/**
 * Returns the BuilderUser on a User's builderAccounts associated w/ a passed builderId
 */
export function getCurrentBuilderUser(
  user: ClientUserHydratedWithRoles | null,
  builderId?: string,
): ClientBuilderUserHydratedWithRoles | null | undefined {
  if (!user || !builderId) {
    return null;
  }
  const builderUser = user.builderAccounts.find((account) => account.builderId === builderId);

  return builderUser ? { ...builderUser, user } : undefined;
}

/**
 * Determines if a given user should be treated as a system-admin
 *
 * @param user
 * @param ignoreRoleImpersonation - if true, will ignore role impersonation and only check user's role
 *
 * @returns true if should be treated as a system-admin, otherwise false
 */
export function userIsSystemAdmin(
  user?: Pick<ClientUser, "adminRole" | "tempUserRole" | "email">,
  ignoreRoleImpersonation?: boolean,
): boolean {
  if (user?.email && isHigharcDemoAccountEmail(user.email)) {
    return false;
  }

  const roleImpersonationActive = !!user?.tempUserRole && !ignoreRoleImpersonation;

  if (user?.adminRole === "SYSTEM_ADMIN" && !roleImpersonationActive) {
    return true;
  } else {
    return false;
  }
}

function isSuperOrSystemAdmin(adminRole?: UserAdminRole): adminRole is UserAdminOnlyRole {
  return adminRole === "SYSTEM_ADMIN" || adminRole === "SUPER_ADMIN";
}

/**
 * Determines if a given user should be treated as a super-admin
 *
 * @param user
 * @param ignoreRoleImpersonation - if true, will ignore role impersonation and only check user's role
 *
 * @returns true if should be treated as a super-admin, otherwise false
 */
export function userIsSuperAdmin(
  user: Pick<ClientUser, "adminRole" | "tempUserRole" | "email"> | null,
  ignoreRoleImpersonation?: boolean,
): boolean {
  if (user?.email && isHigharcDemoAccountEmail(user.email)) {
    return false;
  }

  const roleImpersonationActive = !!user?.tempUserRole && !ignoreRoleImpersonation;

  if (isSuperOrSystemAdmin(user?.adminRole) && !roleImpersonationActive) {
    return true;
  } else {
    return false;
  }
}

function getSuperAdminRoleOverride(
  user: Pick<ClientUser, "adminRole" | "tempUserRole"> | null,
): string | null | undefined {
  return isSuperOrSystemAdmin(user?.adminRole) ? user?.tempUserRole : undefined;
}

/**
 * This is to support the legacy auth objects in checking if the user is on a builder.
 * @deprecated by permissions usage
 */
export function isUserOnBuilder(
  user: Pick<ClientUser, "adminRole" | "tempUserRole"> | null,
  builderAccounts?: { builderId: string | null }[],
  builderId?: string,
): boolean | null {
  if (userIsSuperAdmin(user)) {
    return true;
  }

  if (!builderAccounts || !builderId) {
    return null;
  }
  const builderUser = builderAccounts.find((account) => account.builderId === builderId);
  return !!builderUser;
}

/**
 * Determines if a user possesses the permission to perform an action:
 *
 * 1. Is the user a super admin? (user.adminRole is not "NONE") and therefore has permission overrides
 * 2. Is the super-admin impersonating a role? (user.tempUserRole) and will map to different permissions
 * 3. What is the user's builder account permission? Determined via cedar policies / roles.
 */
export function userHasPermission(permission: ClientBuilderUserPermission, context: UserHasPermissionContext): boolean {
  const { user, builderUser, builderPoliciesAndRoles, resourceId } = context;

  if (userIsSuperAdmin(user)) {
    return true;
  }

  if (builderPoliciesAndRoles) {
    const adminRole = getSuperAdminRoleOverride(user);

    if (adminRole) {
      const adminRoleId = builderPoliciesAndRoles.roles.find((r) => r.name === adminRole)?.id;
      if (adminRoleId) {
        // We use a placeholder user id here - the result is that the user will be associated with the provided
        // admin role and no other roles or individual policies.
        const hasGlobalPermission = isAuthorized(
          cedarAuthorizationArgsForBuilderUser(
            "adminUser",
            [adminRoleId],
            builderPoliciesAndRoles.policies,
            permission,
          ),
        );

        if (resourceId) {
          return isAuthorized(
            cedarAuthorizationArgsForBuilderUser(
              "adminUser",
              [adminRoleId],
              builderPoliciesAndRoles.policies,
              permission,
              resourceId,
            ),
          );
        }

        if (hasGlobalPermission) {
          return true;
        }

        return hasAnyResourceScopedPermission("adminUser", [adminRoleId], permission, builderPoliciesAndRoles.policies);
      } else {
        console.error(`userHasPermission: admin role ${adminRole} not found in builder policies and roles`);
        return false;
      }
    } else if (builderUser) {
      const roleIds = builderUser.roles.map((r) => r.id);
      const hasGlobalPermission = isAuthorized(
        cedarAuthorizationArgsForBuilderUser(builderUser.id, roleIds, builderPoliciesAndRoles.policies, permission),
      );

      if (resourceId) {
        return isAuthorized(
          cedarAuthorizationArgsForBuilderUser(
            builderUser.id,
            roleIds,
            builderPoliciesAndRoles.policies,
            permission,
            resourceId,
          ),
        );
      }

      if (hasGlobalPermission) {
        return true;
      }

      return hasAnyResourceScopedPermission(builderUser.id, roleIds, permission, builderPoliciesAndRoles.policies);
    }
  }

  return false;
}

export type UserHasPermissionContext = {
  user: Pick<ClientUser, "adminRole" | "tempUserRole"> | null;
  builderUser?: Pick<ClientBuilderUserWithRoles, "id" | "userId" | "roles">;
  builderPoliciesAndRoles?: {
    policies: PolicyJson[];
    roles: Omit<ClientBuilderRole, "description">[];
  };
  resourceId?: string;
};

function actionIncludesPermission(action: PolicyJson["action"], permission: ClientBuilderUserPermission): boolean {
  if (action.op === "All") {
    return true;
  }

  if (action.op === "==") {
    return action.entity.id === permission;
  }

  return action.entities.some((entity) => entity.id === permission);
}

function hasAnyResourceScopedPermission(
  principalId: string,
  roleIds: string[],
  permission: ClientBuilderUserPermission,
  policies: PolicyJson[],
): boolean {
  if (!clientBuilderUserPermissionsWithResourceScopedFallback.includes(permission)) {
    return false;
  }

  const resourceIds = new Set<string>();
  for (const policy of policies) {
    if (!actionIncludesPermission(policy.action, permission)) {
      continue;
    }

    if (!policy.resource || policy.resource.op === "All") {
      continue;
    }

    for (const resourceId of policy.resource.ids) {
      resourceIds.add(resourceId);
    }
  }

  for (const resourceId of resourceIds) {
    const authorized = isAuthorized(
      cedarAuthorizationArgsForBuilderUser(principalId, roleIds, policies, permission, resourceId),
    );

    if (authorized) {
      return true;
    }
  }

  return false;
}

/**
 * Filters policies to only include those relevant to the given user.
 *
 * This function does NOT override Cedar's forbid/permit semantics. Cedar's standard
 * evaluation rules apply:
 *
 *   1. **Forbid always beats permit** - If any matching policy forbids an action,
 *      the action is denied, regardless of any permits (at user or role level).
 *
 *   2. **Permit beats none/default** - If at least one policy permits an action
 *      and no policy forbids it, the action is allowed.
 *
 * User-level policies provide a way to add exceptions for individual users, but they
 * follow the same forbid-beats-permit rule. For example:
 *   - Role permits + User forbids → Denied (forbid wins)
 *   - Role forbids + User permits → Denied (forbid wins)
 *   - Role permits + User permits → Allowed
 *   - Role forbids + User forbids → Denied
 *
 * To grant a user access that their role forbids, the role's forbid policy must be
 * removed or the user must be assigned to a different role.
 */
function filterPoliciesForUser(policies: PolicyJson[], builderUserId: string): PolicyJson[] {
  // Filter to only include policies that apply to this user:
  // 1. User-level policies for this specific user
  // 2. Role-level policies (will be evaluated against user's roles by Cedar)
  // 3. Any other policies (e.g., "All" principal)
  return policies.filter((policy) => {
    // Keep user-level policies only if they're for this specific user
    if (policy.principal.op === "in" && policy.principal.entity.type === "BuilderUser") {
      return policy.principal.entity.id === builderUserId;
    }
    // Keep all role-level and other policies
    return true;
  });
}

export function cedarAuthorizationArgsForBuilderUser(
  builderUserId: string,
  builderUserRoleIds: string[],
  policies: PolicyJson[],
  action: string,
  resource?: string,
): AuthorizationArgs {
  // Filter to policies relevant to this user (Cedar handles forbid/permit precedence)
  const filteredPolicies = filterPoliciesForUser(policies, builderUserId);

  return {
    principal: builderUserId,
    principalType: "BuilderUser",
    roles: builderUserRoleIds,
    roleType: "BuilderRole",
    policies: filteredPolicies,
    action: action,
    resource,
  };
}

/**
 * Determines if a user belongs to a role group, based on the following:
 *
 * 1. Is the user a super admin? (user.superAdmin)
 * 2. Is the user impersonating another role? (user.tempUserRole)
 * 3. What is the user's builder account role? (builderUser.userRole)
 */
export function userCanAccess(user: Pick<ClientUser, "adminRole" | "tempUserRole"> | null): boolean {
  if (userIsSuperAdmin(user)) {
    return true;
  }

  return false;
}

/**
 * Returns the role name for a given user and optional builder account,
 * accounting for role impersonation. (user.tempUserRole)
 */
export function getBuilderUserRoleName(
  user: Pick<ClientUser, "adminRole" | "tempUserRole"> | null,
  builderUser?: Pick<ClientBuilderUserHydratedWithRoles, "roles">,
): string | null {
  if (userIsSuperAdmin(user)) {
    return null;
  }

  if (user?.tempUserRole) {
    return user.tempUserRole;
  }

  // Currently, builder users have exactly one role.
  if (builderUser?.roles?.length && builderUser.roles.length > 0) {
    return builderUser.roles[0].name;
  }

  return null;
}

export type FeatureEnabledSource = "defaultValue" | "environment" | "builder" | "builderUser" | "user";

export interface FeatureEnabledReason {
  source?: FeatureEnabledSource;
  description?: string;
  settings: {
    defaultValue: boolean;
    environment?: boolean;
    builder?: boolean;
    builderUser?: boolean;
    user?: boolean;
  };
}

export type FeatureEnvironmentSettingOverrides = Partial<Record<FeatureName, boolean | undefined>>;

export type FeatureDefaultValueOverrides = Partial<Record<FeatureName, boolean | undefined>>;

/**
 * Determine if a feature is enabled for the current user, in the
 * current context of the builder / page they're accessing. Also
 * includes information about the source of the setting.
 *
 * Order of priority (highest to lowest):
 *
 * 1. Setting for current environment
 * 2. Setting for builder
 * 3. Setting for builder user
 * 4. Setting for user
 * 5. Default value
 *
 * It is important to note that a setting can take three values:
 *
 * 1. true - Enabled
 * 2. false - Disabled
 * 3. null/undefined - Default to a setting with lower precedence, or default
 *
 * The environment setting and the default value are both seeded from code but are
 * DB-authoritative: server callers resolve the DB value (falling back to code) and pass it in via
 * `environmentSettingOverride` / `defaultValueOverride`. This function is client-safe and cannot
 * read the DB itself, so absent an override it uses the code value.
 */
export function featureEnabled(
  user: Pick<ClientUserHydrated, "featureFlags" | "builderAccounts"> | undefined | null,
  builder: Pick<ClientBuilder, "id" | "featureFlags"> | undefined | null,
  featureName: FeatureName | null,
  environmentSettingOverride?: boolean | undefined,
  useEnvironmentSettingOverride = false,
  defaultValueOverride?: boolean | undefined,
): [boolean, FeatureEnabledReason] {
  const { server } = getSharedEnvironment();

  if (!featureName) {
    return [false, { settings: { defaultValue: false } }];
  }

  const feature = features[featureName];
  const defaultValue = defaultValueOverride ?? feature?.settings?.defaultValue;

  const codeEnvironmentSetting = feature?.settings?.environmentValues?.[server];
  const environmentSetting = useEnvironmentSettingOverride ? environmentSettingOverride : codeEnvironmentSetting;
  const userSetting = user?.featureFlags?.[featureName];

  let builderUserSetting: boolean | undefined;
  let builderSetting: boolean | undefined;

  if (builder) {
    const builderUser = user?.builderAccounts?.find((bu) => bu.builderId === builder.id);

    builderUserSetting = builderUser?.featureFlags?.[featureName];
    builderSetting = builder?.featureFlags?.[featureName];
  }

  const enabled = !!(environmentSetting ?? builderSetting ?? builderUserSetting ?? userSetting ?? defaultValue);
  const setting = enabled ? "Enabled" : "Disabled";

  const reason: FeatureEnabledReason = {
    source: "defaultValue",
    description: `${setting} by default.`,
    settings: {
      defaultValue,
      environment: environmentSetting,
      builder: builderSetting,
      builderUser: builderUserSetting,
      user: userSetting,
    },
  };

  if (typeof userSetting === "boolean") {
    reason.source = "user";
    reason.description = `${setting} for this user.`;
  }

  if (typeof builderUserSetting === "boolean") {
    reason.source = "builderUser";
    reason.description = `${setting} for this builder user.`;
  }

  if (typeof builderSetting === "boolean") {
    reason.source = "builder";
    reason.description = `${setting} for entire builder.`;
  }

  if (typeof environmentSetting === "boolean") {
    reason.source = "environment";
    reason.description = `${setting} for entire server.`;
  }

  return [enabled, reason];
}

type EnabledFeatureSet = { [key in `${FeatureName}Enabled`]: boolean };

export function featureSetEnabled(user: ClientUserHydrated, builder: ClientBuilder, featureNames: FeatureName[]) {
  const featuresEnabled = {} as EnabledFeatureSet;

  for (const featureName of featureNames) {
    featuresEnabled[`${featureName}Enabled`] = featureEnabled(user, builder, featureName)[0];
  }

  return featuresEnabled;
}

/**
 * Synchronous builder-level flag check. This runs on the client and therefore cannot read the
 * database, so by default it only sees the code environment value and code default. Server code
 * that needs the DB-authoritative values to win must resolve them first (they depend only on the
 * flag and environment, not the builder) and pass them in via `environmentSettingOverride` /
 * `useEnvironmentSettingOverride` / `defaultValueOverride` — see
 * `featureFlagsService.isBuilderFeatureFlagEnabled`.
 */
export function isFeatureFlagEnabled(
  featureName: FeatureName,
  featureFlags: ClientBuilder["featureFlags"],
  environmentSettingOverride?: boolean | undefined,
  useEnvironmentSettingOverride = false,
  defaultValueOverride?: boolean | undefined,
): boolean {
  return featureEnabled(
    null,
    { id: "", featureFlags },
    featureName,
    environmentSettingOverride,
    useEnvironmentSettingOverride,
    defaultValueOverride,
  )[0];
}

/**
 * Determines whether or not to show features hidden behind a
 * product at the builder level.
 *
 * Super Admins can always see products, unless they are using
 * role impersonation.
 */
export function productEnabled(builder: ClientBuilder | undefined, product: BuilderProduct | undefined): boolean {
  if (!product) {
    return true;
  }

  return !!builder?.enabledProducts?.[product];
}

export function getCurrentUserEnabledProducts(builder?: ClientBuilder): BuilderProducts {
  const enabledProducts = {} as BuilderProducts;

  if (builder) {
    builderProductValues.forEach((product: BuilderProduct) => {
      if (productEnabled(builder, product)) {
        enabledProducts[product] = true;
      }
    });
  }

  return enabledProducts;
}

export function getUserEnabledFeatures(
  user: ClientUserHydrated | null,
  builder?: ClientBuilder,
  environmentSettingOverrides?: FeatureEnvironmentSettingOverrides,
  defaultValueOverrides?: FeatureDefaultValueOverrides,
): FeatureSet {
  const enabledFeatures = {} as FeatureSet;
  const useEnvironmentSettingOverrides = environmentSettingOverrides !== undefined;

  featureNames.forEach((featureName) => {
    const environmentOverride = environmentSettingOverrides?.[featureName];
    const defaultValueOverride = defaultValueOverrides?.[featureName];

    if (
      featureEnabled(
        user,
        builder,
        featureName,
        environmentOverride,
        useEnvironmentSettingOverrides,
        defaultValueOverride,
      )[0]
    ) {
      enabledFeatures[featureName] = true;
    }
  });

  return enabledFeatures;
}

export function clientBuilderFromFeatureFlags(featureFlags: unknown): Pick<ClientBuilder, "id" | "featureFlags"> {
  return { id: "", featureFlags: (featureFlags ?? {}) as FeatureSet };
}

/**
 * Computes the set of features enabled for a builder, ignoring user-level overrides.
 * The builder's feature flags may be sparse; `featureEnabled` resolves missing flags from the
 * environment and default settings.
 */
export function getBuilderEnabledFeatures(builder?: Pick<ClientBuilder, "id" | "featureFlags">): FeatureSet {
  return featureNames.reduce((enabledFeatures, featureName) => {
    if (featureEnabled(null, builder, featureName)[0]) {
      enabledFeatures[featureName] = true;
    }
    return enabledFeatures;
  }, {} as FeatureSet);
}
